import React, { memo } from 'react'
import { MapMarker } from 'transfix-ui/components/MapMarker'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Skeleton } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { IAddress, IMarketplaceStops } from 'types/FMSGraphqlTypes'
import { capitalize } from 'utils/capitalize'
import { getMapMarkerType } from '../../utils'

interface IShipmentLaneStop extends Pick<IMarketplaceStops, 'stopType'> {
  address: Pick<IAddress, 'locality' | 'subdivisionCode'>
}

export interface IShipmentLaneDividerProps {
  stops?: IShipmentLaneStop[]
  /**
   * @default 'shipment-lane-divider'
   */
  dataTest?: string
  /**
   * @default false
   */
  loading?: boolean
}

const useShipmentLaneDividerStyles = makeStyles(theme => ({
  skeletonHr: {
    width: '2px',
    height: theme.spacing(5),

    [theme.breakpoints.up('md')]: {
      width: theme.spacing(6),
      height: '2px',
    },
  },
  hr: {
    backgroundColor: brandColors.coolGray4,
    display: 'inline-block',
    margin: 0,
    width: '2px',

    '&.multiStop': {
      height: theme.spacing(1.25),

      [theme.breakpoints.up('md')]: {
        width: theme.spacing(2),
        height: '2px',
      },
    },
    '&.singleStop': {
      height: theme.spacing(5),

      [theme.breakpoints.up('md')]: {
        width: theme.spacing(6),
        height: '2px',
      },
    },
  },
  flex: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  stopCount: {
    display: 'flex',
    alignItems: 'center',
    padding: `0 ${theme.spacing(0.5)}`,
    backgroundColor: brandColors.coolGray4,
    color: brandColors.white,
    borderRadius: theme.shape.borderRadius,
    height: theme.spacing(2.5),

    [theme.breakpoints.up('md')]: {
      height: 'auto',
    },
  },
  stopsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  singleStop: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
}))

const ShipmentLaneDivider = memo<IShipmentLaneDividerProps>(
  ({ stops = [], dataTest = 'shipment-lane-divider', loading = false }) => {
    const classes = useShipmentLaneDividerStyles()

    const stopsBetween = stops.length - 2
    const isMultiStop = stopsBetween > 0

    if (loading) {
      return <Skeleton className={classes.skeletonHr} />
    }

    return isMultiStop ? (
      <Tooltip
        type='light'
        position='bottom'
        effect='solid'
        trigger={
          <div className={classes.flex} data-test={dataTest}>
            <hr className={`${classes.hr} multiStop`} />
            <div className={classes.stopCount}>{stopsBetween}</div>
            <hr className={`${classes.hr} multiStop`} />
          </div>
        }>
        <div className={classes.stopsList} data-test={`${dataTest}-content`}>
          {stops.map(({ address, stopType }) => {
            const { locality: city, subdivisionCode: stateCode } = address || {}
            const location = city ? `${capitalize(city)}, ${stateCode}` : stateCode
            const mapMarkerType = getMapMarkerType(stopType)

            return (
              <div key={`${dataTest}-${stopType}`} className={classes.singleStop}>
                <MapMarker mini type={mapMarkerType} />
                <Typography variant='caption'>{location}</Typography>
              </div>
            )
          })}
        </div>
      </Tooltip>
    ) : (
      <div className={classes.flex} data-test={dataTest}>
        <hr className={`${classes.hr} singleStop`} />
      </div>
    )
  }
)

export default ShipmentLaneDivider
